import { FormGroup } from '@mui/material';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useLanguage } from '../../../../../contexts/languageContext';
import { fontWeight, TypographyStyledGlobal, colors } from '../../../../../theme';
import useFetcher from '../../../../../utils/hooks/useFetcher';
import { DashboardParams } from '../../../../Dashboard/dashboardInterfaces';
import { adaptTranslationByChartTypeTranslationByChartType, CALCULATION_MODE, CHART_INDICATOR_NAME, CreateIndicatorContext, QUESTION_TYPE, KPI_STATE } from '../../constants';
import { AddIndicatorProps, CrosstableConfigurationProps, SelectedQuestionProps, Quantile } from '../../dashboardModalInterface';
import ActionsButton from '../ActionsButton/actionButton';
import CalculationMode from './NumericIndicator/calculationMode';
import CheckboxButtons from './NumericIndicator/checkboxButtons';
import CustomTitleInput from './NumericIndicator/customTitleInput';
import DecimalRadioButtons from './NumericIndicator/decimalRadioButtons';
import QuantileRadioButtons from './NumericIndicator/quantileRadioButtons';
import QuantilesDropdown from './NumericIndicator/quantilesDropdown';
import ResultNumericIndicator from './NumericIndicator/resultNumericIndicator';

const AddNumericIndicator: FC<AddIndicatorProps> = ({
    createIndicator,
    selectedQuestion,
    selectedRowQuestion,
    setCurrentContent,
    setCreateIndicator,
    setOpenModal,
    displayType,
    isEdit,
    isCrossing,
}) => {
    const { surveyId, dashboardId } = useParams<DashboardParams>();
    const { selectedLanguage } = useLanguage();
    const selectedLanguageForSurvey = selectedLanguage && selectedLanguage.surveysLanguagesIds.find((surveyLanguage) => String(surveyLanguage.surveyId) === surveyId);
    const { data } = useSWR((selectedLanguageForSurvey && !selectedLanguageForSurvey.isDefault) ? `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/questions/?translate=${selectedLanguageForSurvey.languageId}` : `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}/questions/`, useFetcher());
    const quantilesQuestions: SelectedQuestionProps[] = data && data.filter((question: SelectedQuestionProps) => (question.is_for_quartile));
    const { t } = useTranslation(['common', 'dashboard']);
    const [calculationMode, setCalculationMode] = useState<CALCULATION_MODE>(createIndicator.computeMethod as CALCULATION_MODE || CALCULATION_MODE.AVERAGE);
    const [resultView, setResultView] = useState<boolean>(isEdit ? createIndicator.displayModality.isInteger : false);
    const [hasQuartilesView, setHasQuartilesView] = useState<boolean>((isEdit && createIndicator.quartile && Object.keys(createIndicator.quartile).length > 0) || false);
    const [crosstableConfiguration, setCrosstableConfiguration] = useState<CrosstableConfigurationProps>((isEdit && createIndicator.crosstableConfig) ? createIndicator.crosstableConfig : { has_nps: false, has_mean: false });
    const { setSendCreateIndicator } = useContext(CreateIndicatorContext);
    const [selectedQuartileQuestion, setSelectedQuartileQuestion] = useState<SelectedQuestionProps |undefined>((isEdit && quantilesQuestions && createIndicator.quartile && Object.keys(createIndicator.quartile).length > 0) ? quantilesQuestions.find((q) => (q.question_id === createIndicator.quartile?.question_id)) : quantilesQuestions && quantilesQuestions[0]);

    useEffect(() => {
        if (selectedQuestion.type === QUESTION_TYPE.CLOSED_DATA) {
            if (!isEdit) {
                setCalculationMode(CALCULATION_MODE.PERCENTAGE);
                setCreateIndicator((prev) => ({ ...prev, kpiType: 'CLOSED_DATA', displayModality: { ...prev.displayModality, isInteger: resultView }, computeMethod: CALCULATION_MODE.PERCENTAGE }));
            } else {
                const computeMethodTmp = createIndicator.computeMethod as CALCULATION_MODE;
                setCalculationMode(computeMethodTmp);
                setCreateIndicator((prev) => {
                    const newCreateIndicator = { ...prev, kpiType: 'CLOSED_DATA', displayModality: { ...prev.displayModality, isInteger: resultView }, computeMethod: computeMethodTmp };

                    return newCreateIndicator;
                });
            }
        } else {
            setCreateIndicator((prev) => ({ ...prev, kpiType: 'NUMERIC', displayModality: { ...prev.displayModality, isInteger: resultView }, computeMethod: CALCULATION_MODE.AVERAGE }));
        }
    }, []);

    useEffect(() => {
        if (quantilesQuestions && createIndicator.quartile && Object.keys(createIndicator.quartile).length > 0) {
            setSelectedQuartileQuestion(quantilesQuestions.find((q) => (q.question_id === createIndicator.quartile?.question_id)));
        }
    }, [quantilesQuestions]);

    useEffect(() => {
        if (createIndicator.displayType === CHART_INDICATOR_NAME.CROSS_TABLE) {
            setCreateIndicator((prev) => ({ ...prev, crosstableConfig: crosstableConfiguration }));
        }
    }, [crosstableConfiguration]);

    useEffect(() => {
        if (!hasQuartilesView) {
            setCreateIndicator((prev) => ({ ...prev, quartile: undefined }));
        }
        if (hasQuartilesView && selectedQuartileQuestion) {
            const quantileToApply: Quantile = { question_id: selectedQuartileQuestion.question_id, number_of_quantiles: 4 };
            setCreateIndicator((prev) => ({ ...prev, quartile: quantileToApply }));
        }
    }, [hasQuartilesView]);

    const handleCreation = () => {
        if (createIndicator.displayType === CHART_INDICATOR_NAME.KEY_FIGURE || createIndicator.displayType === CHART_INDICATOR_NAME.LINE_CHART) {
            setCreateIndicator((prev) => {
                const newKpiModalityConfig = prev.kpiModalityConfig?.map((item) => {
                    const codeToCheck = createIndicator.displayModality.donutKeyModality.length === 1 ? createIndicator.displayModality.donutKeyModality[0] : createIndicator.displayModality.donutKeyModality;
                    if (item.modalityCode !== codeToCheck) {
                        // if modality state is not excluded we have to hide it. The displayed result in card need to be the selected one.
                        return { ...item, state: item.state !== KPI_STATE.EXCLUDED ? KPI_STATE.HIDDEN : item.state };
                    }

                    return item;
                });
                const newKpiGroupConfig = prev.kpiGroupConfig?.map((item) => {
                    if (JSON.stringify(item.modalityCodes) !== JSON.stringify(createIndicator.displayModality.donutKeyModality)) {
                        // if modality state is not excluded we have to hide it. The displayed result in card need to be the selected one.
                        return { ...item, state: item.state !== KPI_STATE.EXCLUDED ? KPI_STATE.HIDDEN : item.state };
                    }

                    return item;
                });

                return { ...prev, kpiModalityConfig: newKpiModalityConfig, kpiGroupConfig: newKpiGroupConfig };
            });
        }
        if (createIndicator.displayModality === undefined) {
            setCreateIndicator((prev) => ({ ...prev, displayModality: { ...prev.displayModality, isInteger: false } }));
        }
        setOpenModal(false);
        setCurrentContent('sendIndicator');
        setSendCreateIndicator(true);
    };

    const handleSelectedQuartileQuestion = (quartileQuestion: SelectedQuestionProps) => {
        const quantileToApply: Quantile = { question_id: quartileQuestion.question_id, number_of_quantiles: 4 };
        setSelectedQuartileQuestion(quartileQuestion);
        if (hasQuartilesView) {
            setCreateIndicator((prev) => ({ ...prev, quartile: quantileToApply }));
        }
    };

    return (
        <FormGroup sx={{ width: '100%' }}>
            <CustomTitleInput
                defaultValue={createIndicator.customItemTitle}
                displayType={displayType}
                setCreateIndicator={setCreateIndicator}
            />
            {createIndicator.displayType === CHART_INDICATOR_NAME.DONUT && (
                <TypographyStyledGlobal
                    color={colors.black000}
                    margin='15px 0 12px 0'
                    size='18px'
                    weight={fontWeight.semiBold}
                >
                    {t('dashboard:addIndicatorModal.addDonutIndicator.selectKeyFigureTitle')}
                </TypographyStyledGlobal>
            )}
            {createIndicator.displayType !== CHART_INDICATOR_NAME.HORIZONTAL_BAR && createIndicator.displayType !== CHART_INDICATOR_NAME.CROSS_TABLE && (
                <CalculationMode
                    calculationMode={calculationMode}
                    createIndicator={createIndicator}
                    isEdit={isEdit}
                    selectedQuestion={selectedQuestion}
                    setCalculationMode={setCalculationMode}
                    setCreateIndicator={setCreateIndicator}
                />
            )}
            {createIndicator.displayType === CHART_INDICATOR_NAME.CROSS_TABLE && (selectedRowQuestion?.has_numeric_values || selectedRowQuestion?.is_nps) && (
                <CheckboxButtons
                    configuration={crosstableConfiguration}
                    hasNumericValue={selectedRowQuestion?.has_numeric_values}
                    isNps={selectedRowQuestion?.is_nps}
                    setConfiguration={setCrosstableConfiguration}
                />
            )}
            {(quantilesQuestions && quantilesQuestions.length > 0 && createIndicator.displayType === CHART_INDICATOR_NAME.KEY_FIGURE) && (
                <>
                    <QuantileRadioButtons
                        hasQuantilesView={hasQuartilesView}
                        setHasQuantileView={setHasQuartilesView}
                    />
                    <QuantilesDropdown
                        handleSelectedQuartileQuestion={handleSelectedQuartileQuestion}
                        hasQuartilesView={hasQuartilesView}
                        quantilesQuestions={quantilesQuestions}
                        selectedQuartileQuestion={selectedQuartileQuestion}
                    />
                </>
            )}

            <DecimalRadioButtons
                createIndicator={createIndicator}
                isIntegerView={resultView}
                setCreateIndicator={setCreateIndicator}
                setIsIntegerView={setResultView}
            />

            {0 > 1 && (
                <ResultNumericIndicator value={resultView ? 93 : 93.4} />
            )}

            <ActionsButton
                handleClickBack={() => setCurrentContent(isCrossing ? 'refineCrossingIndicatorAnswer' : 'selectDisplayType')}
                handleClickNext={handleCreation}
                isDisabled={calculationMode.length <= 0}
                labelBack={t('common:back')}
                labelNext={adaptTranslationByChartTypeTranslationByChartType(displayType, isEdit).button}
            />

        </FormGroup>

    );
};

export default AddNumericIndicator;
